import React, { useEffect, useState } from 'react';
import { Phone, Instagram, Facebook, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type LegalKey = 'terminos' | 'privacidad';
const LEGAL_CONTENT: Record<LegalKey, {
  title: string;
  body: React.ReactNode;
}> = {
  terminos: {
    title: 'Términos y Condiciones',
    body: <div className="space-y-5 text-slate-300 leading-relaxed" data-id="element-104">
        <p data-id="element-105">
          Bienvenido al sitio de Nacho Gomez. Al acceder y utilizar este sitio
          aceptas los siguientes términos y condiciones. Si no estás de acuerdo,
          te pedimos no continuar navegando.
        </p>
        <div data-id="element-106">
          <h3 className="text-white font-semibold mb-2" data-id="element-107">1. Servicios</h3>
          <p data-id="element-108">
            Ofrezco servicios de diseño web estratégico y consultoría digital
            para emprendedores y negocios de servicios. El alcance, plazos y
            entregables de cada proyecto se definen por escrito previo al inicio
            del trabajo.
          </p>
        </div>
        <div data-id="element-109">
          <h3 className="text-white font-semibold mb-2" data-id="element-110">
            2. Propiedad intelectual
          </h3>
          <p data-id="element-111">
            Todos los contenidos publicados en este sitio (textos, imágenes,
            marca y diseño) son propiedad de Nacho Gomez y no pueden ser
            reproducidos sin autorización.
          </p>
        </div>
        <div data-id="element-112">
          <h3 className="text-white font-semibold mb-2" data-id="element-113">
            3. Limitación de responsabilidad
          </h3>
          <p data-id="element-114">
            La información del sitio se ofrece a título informativo. No me hago
            responsable por decisiones tomadas únicamente en base al contenido
            aquí publicado sin una asesoría personalizada.
          </p>
        </div>
        <div data-id="element-115">
          <h3 className="text-white font-semibold mb-2" data-id="element-116">4. Modificaciones</h3>
          <p data-id="element-117">
            Estos términos pueden actualizarse en cualquier momento. La versión
            vigente será siempre la publicada en este sitio.
          </p>
        </div>
      </div>
  },
  privacidad: {
    title: 'Política de Privacidad',
    body: <div className="space-y-5 text-slate-300 leading-relaxed" data-id="element-118">
        <p data-id="element-119">
          Tu privacidad es importante. Esta política explica qué información
          recopilo y cómo la utilizo cuando interactúas con este sitio.
        </p>
        <div data-id="element-120">
          <h3 className="text-white font-semibold mb-2" data-id="element-121">
            1. Información que recopilo
          </h3>
          <p data-id="element-122">
            Solo recopilo los datos que voluntariamente entregas (por ejemplo:
            nombre, correo electrónico o número de WhatsApp) al contactarme para
            una consulta o cotización.
          </p>
        </div>
        <div data-id="element-123">
          <h3 className="text-white font-semibold mb-2" data-id="element-124">
            2. Uso de la información
          </h3>
          <p data-id="element-125">
            Utilizo tus datos exclusivamente para responder a tu consulta, hacer
            seguimiento comercial y, eventualmente, enviarte información
            relevante sobre mis servicios. Nunca venderé ni compartiré tu
            información con terceros.
          </p>
        </div>
        <div data-id="element-126">
          <h3 className="text-white font-semibold mb-2" data-id="element-127">
            3. Cookies y analítica
          </h3>
          <p data-id="element-128">
            Este sitio puede utilizar cookies y herramientas de analítica (como
            Google Analytics o píxeles de Meta) para entender el comportamiento
            de los visitantes y mejorar la experiencia.
          </p>
        </div>
        <div data-id="element-129">
          <h3 className="text-white font-semibold mb-2" data-id="element-130">4. Tus derechos</h3>
          <p data-id="element-131">
            Puedes solicitar en cualquier momento el acceso, rectificación o
            eliminación de tus datos escribiéndome por WhatsApp o correo
            electrónico.
          </p>
        </div>
      </div>
  }
};
export function Footer() {
  const [openModal, setOpenModal] = useState<LegalKey | null>(null);
  // Lock body scroll while modal is open + close with ESC
  useEffect(() => {
    if (!openModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [openModal]);
  const current = openModal ? LEGAL_CONTENT[openModal] : null;
  return <footer className="bg-[#050810] py-12 border-t border-white/5" data-id="element-132">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-133">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-134">
          <div className="flex flex-col items-center md:items-start gap-4" data-id="element-135">
            <img src="https://cdn.magicpatterns.com/uploads/msY3D9KKyvrQfPwXBNDEaP/logo_recortado_nacho.png" alt="Nacho Gomez Logo" className="h-16 md:h-20 w-auto object-contain mix-blend-screen opacity-90 hover:opacity-100 transition-opacity" data-id="element-136" />
            <p className="text-sm text-slate-500" data-id="element-137">
              Diseño Web Estratégico para Emprendedores
            </p>
          </div>

          <div className="flex items-center gap-6 text-slate-400" data-id="element-138">
            <a href="https://wa.me/56972115173" target="_blank" rel="noreferrer" className="hover:text-brand-accent transition-colors" data-id="element-139">
              <span className="sr-only" data-id="element-140">WhatsApp</span>
              <Phone className="w-5 h-5" data-id="element-141" />
            </a>
            <a href="https://www.instagram.com/nachogomezmkt/" target="_blank" rel="noreferrer" className="hover:text-brand-accent transition-colors" data-id="element-142">
              <span className="sr-only" data-id="element-143">Instagram</span>
              <Instagram className="w-5 h-5" data-id="element-144" />
            </a>
            <a href="https://web.facebook.com/nachogomezmkt/" target="_blank" rel="noreferrer" className="hover:text-brand-accent transition-colors" data-id="element-145">
              <span className="sr-only" data-id="element-146">Facebook</span>
              <Facebook className="w-5 h-5" data-id="element-147" />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 text-center md:text-left text-sm text-slate-600 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-148">
          <p data-id="element-149">
            © {new Date().getFullYear()} Nacho Gomez. Todos los derechos
            reservados.
          </p>
          <div className="flex gap-4" data-id="element-150">
            <button type="button" onClick={() => setOpenModal('terminos')} className="hover:text-slate-400 transition-colors" data-id="element-151">
              Términos
            </button>
            <button type="button" onClick={() => setOpenModal('privacidad')} className="hover:text-slate-400 transition-colors" data-id="element-152">
              Privacidad
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-153">
        {current && <motion.div key="legal-modal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-154">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpenModal(null)} aria-hidden="true" data-id="element-155" />
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative w-full max-w-2xl max-h-[85vh] bg-brand-card border border-white/10 rounded-2xl shadow-2xl flex flex-col" data-id="element-156">
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/5" data-id="element-157">
                <h2 id="legal-modal-title" className="text-xl font-bold text-white" data-id="element-158">
                  {current.title}
                </h2>
                <button type="button" onClick={() => setOpenModal(null)} className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-white/5 transition-colors" aria-label="Cerrar" data-id="element-159">
                  <X className="w-5 h-5" data-id="element-160" />
                </button>
              </div>
              <div className="px-6 py-6 overflow-y-auto text-sm" data-id="element-161">
                {current.body}
              </div>
              <div className="px-6 py-4 border-t border-white/5 flex justify-end" data-id="element-162">
                <button type="button" onClick={() => setOpenModal(null)} className="text-sm font-semibold text-slate-300 hover:text-white px-4 py-2 rounded-md hover:bg-white/5 transition-colors" data-id="element-163">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </footer>;
}